import { Button, Fab, Icon, makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import React, { CSSProperties, useMemo } from 'react';
import { findSelectedEntityNode } from '.';
import { EntitiesListingDataItem, EntitiesListingViewType } from '..';
import { EntityBasicViewUIComponent, EntityBasicViewUIComponentCustomSettings, EntityNode } from '../../containers';
import { ReadonlyNonEmptyArray } from '../../utils/utils';


const useStyle = makeStyles((theme: Theme) => {
    return {

        root: {
            display: 'grid',
            gap    : theme.spacing(1),
        },

        cardListContainer: {
            gridTemplateColumns: '100%',
        },

        cardGridContainer: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            '&.medium'         : {
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            },
            '&.large': {
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            },
        },

        entityCardWrapper: {
            position          : 'relative',
            '&:hover, :active': {
                '& $previewButton': {
                    visibility: 'visible',
                },
            },
        },

        entityComp: {
            '&.selected': {
                boxShadow: theme.shadows[5],
            },
        },

        previewButton: {
            visibility: 'hidden',
            position  : 'absolute',
            top       : theme.spacing(1),
            right     : theme.spacing(1),
        },

        loadMoreButton: {
            width    : 'fit-content',
            height   : 'fit-content',
            alignSelf: 'flex-end',
        },

        emptyMessage: {
            flex     : 1,
            alignSelf: 'center',
        },

        [theme.breakpoints.up('md')]: {
            root: {
                gap: theme.spacing(2),
            },
        },


        [theme.breakpoints.up('lg')]: {
            root: {
                gap: theme.spacing(3),
            },
        },
    };
}, { name: 'EntitlesListingCardViewComp', index: 1 });



/**
 * The props required by the EntitiesListingCardViewComp components.
 */
export interface EntitiesListingCardViewCompProps {

    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;
    readonly viewType: EntitiesListingViewType.LIST | EntitiesListingViewType.GRID;

    // The array of entities data items.
    readonly listData: ReadonlyNonEmptyArray<EntitiesListingDataItem>;

    // The entityId of the current selected entity.
    readonly selectedEntityId?: string | undefined;

    readonly cardViewSettings?: {
        readonly cardCompSettings?: EntityBasicViewUIComponentCustomSettings | undefined;
    } | undefined;

    readonly texts: {
        readonly loadMoreLabel: string;
    }

    // The handler for when the user select an entity
    readonly onEntitySelected?: ((entityNode: EntityNode)=> void) | undefined;

    // The handler for when the user click on the preview button
    readonly onPreviewItemClicked?: (entityNode: EntityNode)=> void;


    // The entity to use as current root. When in LIST or GRID view, the user can select any entity as root. This is similar to how file browsers
    // work which let the user to enter a sub directory to view the directory's contents. So if the user select a different entity as root,
    // we will show only the children of the subRootEntityId
    readonly subRootEntityId?: string | undefined;

    // The component to show when there is no data
    readonly noDataMessageComp: React.ReactNode;

    // The handler for when the user select another entity as root
    readonly onChangeRootEntity?: ((entityNode: EntityNode)=> void) | undefined;
}


/**
 * This is a component that display list of entities using Card component.
 */
export const EntitiesListingCardViewComp: React.FC<EntitiesListingCardViewCompProps> = (props: EntitiesListingCardViewCompProps) => {
    const classes = useStyle();


    const rootEntityNode = useMemo(() => {
        return props.listData[0];
    }, [props.listData]);


    /**
     * If subRootEntityId is provided, find the EntityNode that contain the entity with id subRootEntityId
     */
    const subRootEntityNode = useMemo(() => {
        if (props.subRootEntityId) {
            return findSelectedEntityNode(props.subRootEntityId, props.listData[0]);
        }
        return undefined;
    }, [props.subRootEntityId, props.listData]);


    const children = useMemo(() => {
        return (subRootEntityNode ?? rootEntityNode).displayValue.children;
    }, [rootEntityNode, subRootEntityNode]);


    return (
        <div>
            {children && children.length > 0 && (
                <div
                    className={clsx(
                        props.className,
                        classes.root,
                        props.viewType === EntitiesListingViewType.LIST && classes.cardListContainer,
                        props.viewType === EntitiesListingViewType.GRID && classes.cardGridContainer,
                    )}
                    style={props.style}
                >
                    {/* Is there is a subRootEntityNode, display its' children. Else, display the rootNode's children */}
                    {children.map((child) => {
                        if (child.isLoadMoreItem) {
                            return (
                                <Button
                                    key={child.actualValue.entity.entityId}
                                    className={classes.loadMoreButton}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        if (props.onEntitySelected) {
                                            props.onEntitySelected(child.actualValue);
                                        }
                                    }}
                                >
                                    {props.texts.loadMoreLabel}
                                </Button>
                            );
                        }
                        return (
                            <div
                                className={classes.entityCardWrapper}
                                key={child.actualValue.entity.entityId}
                            >
                                <EntityBasicViewUIComponent
                                    className={clsx(classes.entityComp, props.selectedEntityId === child.actualValue.entity.entityId && 'selected')}
                                    projectId={props.projectId}
                                    entityId={child.actualValue.entity.entityId}
                                    customSettings={props.cardViewSettings?.cardCompSettings}
                                    onClick={() => {
                                        if (props.onChangeRootEntity) {
                                            props.onChangeRootEntity(child.actualValue);
                                        }
                                    }}
                                />
                                <Fab
                                    className={classes.previewButton}
                                    color="default"
                                    size="small"
                                    onClick={() => {
                                        if (props.onPreviewItemClicked) {
                                            props.onPreviewItemClicked(child.actualValue);
                                        }
                                    }}
                                >
                                    <Icon fontSize="small">visibility</Icon>
                                </Fab>
                            </div>
                        );
                    })}
                </div>
            )}
            {(!children || children.length === 0) && (
                <div className={classes.emptyMessage}>
                    {props.noDataMessageComp}
                </div>
            )}
        </div>
    );
};
